import type { LucideIcon } from 'lucide-react';
import { File, FileCode, FileSpreadsheet, FileText, FileType2 } from 'lucide-react';

/**
 * Pretty-print a byte count as a short human string ("1.8 MB").
 *
 * Returns ``"—"`` when the value is missing so callers can pipe unknown
 * sizes directly into a template without conditional rendering.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log10(bytes) / 3));
  const scaled = bytes / 1000 ** i;
  const decimals = scaled >= 100 || i === 0 ? 0 : 1;
  return `${scaled.toFixed(decimals)} ${units[i]}`;
}

/**
 * Short relative date ("3 days ago", "just now").
 *
 * Accepts an ISO-8601 timestamp (stored on each Weaviate chunk as
 * ``uploaded_at``). Returns ``"—"`` when the input is missing so the UI
 * stays stable on legacy docs that predate the metadata change.
 */
export function formatRelativeDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '—';
  const diffSec = Math.max(0, (Date.now() - ts) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86_400) return `${Math.floor(diffSec / 3600)} hr ago`;
  const days = Math.floor(diffSec / 86_400);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (days < 30) return `${Math.floor(days / 7)} wk ago`;
  if (days < 365) return `${Math.floor(days / 30)} mo ago`;
  return `${Math.floor(days / 365)} yr ago`;
}

type IconSpec = { icon: LucideIcon; className: string };

/**
 * Map a lowercase file extension (``"pdf"``, ``"md"``, ...) to a lucide icon
 * and tailwind colour. Falls back to a generic ``File`` in ``muted``.
 */
export function extensionToIcon(fileType: string | null | undefined): IconSpec {
  switch ((fileType || '').toLowerCase()) {
    case 'pdf':
      return { icon: FileText, className: 'text-destructive' };
    case 'md':
    case 'markdown':
      return { icon: FileCode, className: 'text-primary' };
    case 'docx':
    case 'doc':
      return { icon: FileType2, className: 'text-primary' };
    case 'csv':
    case 'xlsx':
      return { icon: FileSpreadsheet, className: 'text-muted-foreground' };
    case 'txt':
    case 'xml':
      return { icon: FileText, className: 'text-muted-foreground' };
    default:
      return { icon: File, className: 'text-muted-foreground' };
  }
}
